import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Download, Loader2, LayoutTemplate, Settings, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import API from '@/config/api';

const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const PosterBuilderDialog = ({ open, onOpenChange }) => {
  const { getAuthHeaders } = useAuth();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [vacationStart, setVacationStart] = useState('');
  const [vacationEnd, setVacationEnd] = useState('');
  const [vacationReopen, setVacationReopen] = useState('');
  const [events, setEvents] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftMeta, setDraftMeta] = useState(null); // { updated_at } when a draft is loaded

  // Inline edits for the persistent-settings fields — bootstrapped from
  // /poster/defaults so the admin sees their current values and can tweak
  // them just for this month's poster.
  const [editOpen, setEditOpen] = useState(false);
  const [saveAsDefaults, setSaveAsDefaults] = useState(false);
  const [edit, setEdit] = useState({
    tagline: '', age_range_served: '', hours_line: '',
    important_reminders: '', transportation_reminder: '',
    what_to_bring_daily: '', program_highlights: '',
  });

  const addEvent = () => setEvents([...events, { title: '', date: '', description: '' }]);
  const updateEvent = (i, k, v) => { const n = [...events]; n[i] = { ...n[i], [k]: v }; setEvents(n); };
  const removeEvent = (i) => setEvents(events.filter((_, idx) => idx !== i));

  const loadPreview = async () => {
    try {
      const [prevRes, draftRes] = await Promise.all([
        axios.get(`${API}/newsletters/poster/preview?month=${month}&year=${year}`, getAuthHeaders()),
        // Optionally load an existing draft for this month/year — a 404 means "no draft yet", not an error
        axios.get(`${API}/newsletters/poster/draft?month=${month}&year=${year}`, getAuthHeaders()).catch(() => null),
      ]);
      setPreviewData(prevRes.data);
      const d = prevRes.data?.defaults || {};

      const draft = draftRes?.data;
      if (draft) {
        // Hydrate monthly fields from saved draft
        setWelcomeMessage(draft.welcome_message || '');
        setVacationStart(draft.vacation_start || '');
        setVacationEnd(draft.vacation_end || '');
        setVacationReopen(draft.vacation_reopen || '');
        setEvents(Array.isArray(draft.events) ? draft.events : []);
        setDraftMeta({ updated_at: draft.updated_at });
        setEdit({
          tagline: draft.tagline || d.tagline || '',
          age_range_served: draft.age_range_served || d.age_range_served || '',
          hours_line: draft.hours_line || d.hours_line || '',
          important_reminders: Array.isArray(draft.important_reminders) && draft.important_reminders.length
            ? draft.important_reminders.join('\n')
            : (d.important_reminders || []).join('\n'),
          transportation_reminder: draft.transportation_reminder || d.transportation_reminder || '',
          what_to_bring_daily: Array.isArray(draft.what_to_bring_daily) && draft.what_to_bring_daily.length
            ? draft.what_to_bring_daily.join('\n')
            : (d.what_to_bring_daily || []).join('\n'),
          program_highlights: Array.isArray(draft.program_highlights) && draft.program_highlights.length
            ? draft.program_highlights.join('\n')
            : (d.program_highlights || []).join('\n'),
        });
        setSaveAsDefaults(!!draft.save_as_defaults);
      } else {
        // No saved draft for this month — clear monthly fields so switching
        // months doesn't carry over the previous month's inputs.
        setDraftMeta(null);
        setWelcomeMessage('');
        setVacationStart('');
        setVacationEnd('');
        setVacationReopen('');
        setEvents([]);
        setEdit(prev => ({
          tagline: d.tagline ?? prev.tagline,
          age_range_served: d.age_range_served ?? prev.age_range_served,
          hours_line: d.hours_line ?? prev.hours_line,
          important_reminders: (d.important_reminders && d.important_reminders.length) ? d.important_reminders.join('\n') : prev.important_reminders,
          transportation_reminder: d.transportation_reminder ?? prev.transportation_reminder,
          what_to_bring_daily: (d.what_to_bring_daily && d.what_to_bring_daily.length) ? d.what_to_bring_daily.join('\n') : prev.what_to_bring_daily,
          program_highlights: (d.program_highlights && d.program_highlights.length) ? d.program_highlights.join('\n') : prev.program_highlights,
        }));
      }
    } catch {
      setPreviewData(null);
    }
  };

  const buildRequestBody = () => {
    const cleanEvents = events.filter(e => (e.title || '').trim());
    // Defensively coerce arrays back to newline-joined strings so a stray
    // array (e.g. residual initial state) never breaks .split().
    const strToList = (s) => {
      if (Array.isArray(s)) return s.map(x => (x || '').toString().trim()).filter(Boolean);
      return (s || '').toString().split('\n').map(x => x.trim()).filter(Boolean);
    };
    return {
      month,
      year,
      welcome_message: welcomeMessage,
      vacation_start: vacationStart || null,
      vacation_end: vacationEnd || null,
      vacation_reopen: vacationReopen || null,
      events: cleanEvents,
      tagline: edit.tagline || null,
      age_range_served: edit.age_range_served || null,
      hours_line: edit.hours_line || null,
      important_reminders: strToList(edit.important_reminders).length ? strToList(edit.important_reminders) : null,
      transportation_reminder: edit.transportation_reminder || null,
      what_to_bring_daily: strToList(edit.what_to_bring_daily).length ? strToList(edit.what_to_bring_daily) : null,
      program_highlights: strToList(edit.program_highlights).length ? strToList(edit.program_highlights) : null,
      save_as_defaults: saveAsDefaults,
    };
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      await axios.post(`${API}/newsletters/poster/save-draft`, buildRequestBody(), getAuthHeaders());
      toast.success(`${monthNames[month - 1]} ${year} draft saved. You can close and come back later.`);
      setDraftMeta({ updated_at: new Date().toISOString() });
    } catch (e) {
      console.error('saveDraft error:', e, e?.message);
      toast.error(e.response?.data?.detail || e.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  React.useEffect(() => { if (open) loadPreview(); /* eslint-disable-next-line */ }, [open, month, year]);

  const downloadPdf = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/newsletters/poster/pdf`, buildRequestBody(), {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${monthNames[month - 1]}_${year}_Newsletter.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Poster newsletter downloaded!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutTemplate className="w-5 h-5 text-primary" />
            Create Poster Newsletter
          </DialogTitle>
          <DialogDescription>
            Birthdays and classrooms are pulled from your database automatically. Just fill in this month&apos;s welcome message and any special events.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Month</Label>
              <select className="w-full h-10 border rounded-md px-3 bg-background" value={month} onChange={(e) => setMonth(parseInt(e.target.value))} data-testid="poster-month">
                {monthNames.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
              </select>
            </div>
            <div>
              <Label>Year</Label>
              <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value))} data-testid="poster-year" />
            </div>
          </div>

          {previewData && (
            <div className="rounded-lg bg-purple-50 border border-purple-200 p-3 text-sm text-purple-900">
              <p className="font-medium mb-1">Auto-pulled for {monthNames[month - 1]} {year}:</p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li><b>{previewData.birthdays?.length || 0}</b> birthday{(previewData.birthdays?.length !== 1) ? 's' : ''} this month{previewData.birthdays?.length ? ': ' + previewData.birthdays.map(b => `${b.name} (${monthNames[month-1].slice(0,3)} ${b.day})`).join(', ') : ''}</li>
                <li><b>{previewData.classrooms?.length || 0}</b> classroom{(previewData.classrooms?.length !== 1) ? 's' : ''} listed{previewData.classrooms?.length ? ': ' + previewData.classrooms.map(c => c.name).join(', ') : ''}</li>
              </ul>
            </div>
          )}

          <div>
            <Label>Welcome message</Label>
            <Textarea
              rows={4}
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              placeholder={`We hope everyone is having an amazing ${monthNames[month - 1].toLowerCase()}!…`}
              data-testid="poster-welcome"
            />
          </div>

          <div className="space-y-2 border rounded-lg p-3 bg-red-50/40 border-red-200">
            <Label className="text-sm font-semibold text-red-700">Center Vacation / Closure (optional)</Label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Closed from</Label>
                <Input type="date" value={vacationStart} onChange={(e) => setVacationStart(e.target.value)} data-testid="poster-vac-start" />
              </div>
              <div>
                <Label className="text-xs">Closed until</Label>
                <Input type="date" value={vacationEnd} onChange={(e) => setVacationEnd(e.target.value)} data-testid="poster-vac-end" />
              </div>
              <div>
                <Label className="text-xs">Reopen</Label>
                <Input type="date" value={vacationReopen} onChange={(e) => setVacationReopen(e.target.value)} data-testid="poster-vac-reopen" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Upcoming Events (optional)</Label>
              <Button type="button" size="sm" variant="outline" onClick={addEvent} data-testid="poster-add-event">
                <Plus className="w-4 h-4 mr-1" /> Add event
              </Button>
            </div>
            {events.map((e, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 border rounded-md p-2">
                <Input className="col-span-5" placeholder="Title" value={e.title} onChange={(ev) => updateEvent(i, 'title', ev.target.value)} data-testid={`event-title-${i}`} />
                <Input className="col-span-3" placeholder="Date" value={e.date} onChange={(ev) => updateEvent(i, 'date', ev.target.value)} data-testid={`event-date-${i}`} />
                <Input className="col-span-3" placeholder="Details (optional)" value={e.description} onChange={(ev) => updateEvent(i, 'description', ev.target.value)} data-testid={`event-desc-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="col-span-1" onClick={() => removeEvent(i)} data-testid={`remove-event-${i}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>

          <div className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setEditOpen(!editOpen)}
              className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-sm font-medium"
              data-testid="toggle-edit-poster-info"
            >
              <span className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-slate-600" />
                Edit poster info for this month
              </span>
              <span className="text-xs text-muted-foreground">{editOpen ? 'Hide' : 'Show'}</span>
            </button>
            {editOpen && (
              <div className="p-3 space-y-3 text-sm">
                <p className="text-xs text-muted-foreground">
                  Change any of the values below to override your saved Settings just for this PDF. Leave blank to use your saved defaults.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Tagline</Label>
                    <Input value={edit.tagline} onChange={(e) => setEdit({ ...edit, tagline: e.target.value })} data-testid="edit-tagline" />
                  </div>
                  <div>
                    <Label className="text-xs">Ages served</Label>
                    <Input value={edit.age_range_served} onChange={(e) => setEdit({ ...edit, age_range_served: e.target.value })} data-testid="edit-age-range" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Center hours</Label>
                  <Input value={edit.hours_line} onChange={(e) => setEdit({ ...edit, hours_line: e.target.value })} data-testid="edit-hours" />
                </div>
                <div>
                  <Label className="text-xs">Important Reminders <span className="text-muted-foreground font-normal">(one per line)</span></Label>
                  <Textarea rows={5} value={edit.important_reminders} onChange={(e) => setEdit({ ...edit, important_reminders: e.target.value })} data-testid="edit-important-reminders" />
                </div>
                <div>
                  <Label className="text-xs">Transportation Reminder</Label>
                  <Textarea rows={3} value={edit.transportation_reminder} onChange={(e) => setEdit({ ...edit, transportation_reminder: e.target.value })} data-testid="edit-transportation" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">What to Bring Daily <span className="text-muted-foreground font-normal">(one per line)</span></Label>
                    <Textarea rows={5} value={edit.what_to_bring_daily} onChange={(e) => setEdit({ ...edit, what_to_bring_daily: e.target.value })} data-testid="edit-what-to-bring" />
                  </div>
                  <div>
                    <Label className="text-xs">Program Highlights <span className="text-muted-foreground font-normal">(one per line)</span></Label>
                    <Textarea rows={5} value={edit.program_highlights} onChange={(e) => setEdit({ ...edit, program_highlights: e.target.value })} data-testid="edit-program-highlights" />
                  </div>
                </div>
                <label className="flex items-center gap-2 pt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveAsDefaults}
                    onChange={(e) => setSaveAsDefaults(e.target.checked)}
                    data-testid="save-as-defaults-toggle"
                  />
                  <span className="text-xs">Also save these edits as my new defaults</span>
                </label>
              </div>
            )}
          </div>

          <div className="text-xs text-muted-foreground border-t pt-3">
            Missing hours, reminders, or classroom list on the preview?{' '}
            <Link to="/settings?tab=newsletter" className="text-primary underline" onClick={() => onOpenChange(false)}>
              <Settings className="w-3 h-3 inline" /> Configure poster settings
            </Link>.
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          {draftMeta?.updated_at && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mr-auto">
              <RotateCcw className="w-3 h-3" />
              Loaded from a saved draft
            </div>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            type="button"
            variant="outline"
            onClick={saveDraft}
            disabled={savingDraft}
            data-testid="poster-save-draft-btn"
          >
            {savingDraft ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Draft
          </Button>
          <Button type="button" onClick={downloadPdf} disabled={generating} data-testid="poster-download-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PosterBuilderDialog;
